import React, { useCallback, useEffect, useRef, useState } from 'react';
import CameraPlayer, { ERRO_CODEC } from './CameraPlayer';
import { rotuloCompleto } from '../../helpers/camerasLayout';

// Uma camera sozinha, ocupando o palco: stream PRINCIPAL (HD), zoom digital e
// navegacao para a anterior/proxima. O tour fica parado por baixo enquanto
// isto esta aberto.
//
// Zoom e so do navegador (amplia a imagem que ja chegou): nao mexe na camera
// e funciona em qualquer modelo. Por isso a camera abre em HD — ampliar o
// stream extra so mostraria pixel.

const ZOOM_MAX = 8;
const PASSO_ZOOM = 1.25;

export default function CameraUnica({
  camera, cameras, gravadoresPorId, onFechar, onTrocar, onRecortar,
}) {
  const [hd, setHd] = useState(true);
  const [avisoQualidade, setAvisoQualidade] = useState('');
  const [zoom, setZoom] = useState({ s: 1, x: 0, y: 0 });
  const areaRef = useRef(null);
  const arrasto = useRef(null);

  // Trocou de camera: volta ao HD e sem zoom.
  useEffect(() => {
    setHd(true);
    setAvisoQualidade('');
    setZoom({ s: 1, x: 0, y: 0 });
  }, [camera.id]);

  const indice = cameras.findIndex((c) => c.id === camera.id);
  const vizinha = useCallback((passo) => {
    if (cameras.length < 2) return;
    const i = ((indice + passo) % cameras.length + cameras.length) % cameras.length;
    onTrocar(cameras[i]);
  }, [cameras, indice, onTrocar]);

  // Mantem a imagem cobrindo a area: com zoom, nao deixa arrastar para fora.
  const limitar = useCallback(({ s, x, y }) => {
    const area = areaRef.current?.getBoundingClientRect();
    if (!area || s <= 1) return { s: 1, x: 0, y: 0 };
    return {
      s,
      x: Math.min(0, Math.max(area.width - area.width * s, x)),
      y: Math.min(0, Math.max(area.height - area.height * s, y)),
    };
  }, []);

  // Amplia mantendo parado o ponto (px, py) da area — o ponto sob o mouse.
  const ampliarEm = useCallback((fator, px, py) => {
    setZoom((z) => {
      const s = Math.min(ZOOM_MAX, Math.max(1, z.s * fator));
      const mundoX = (px - z.x) / z.s;
      const mundoY = (py - z.y) / z.s;
      return limitar({ s, x: px - mundoX * s, y: py - mundoY * s });
    });
  }, [limitar]);

  const ampliarNoCentro = (fator) => {
    const area = areaRef.current?.getBoundingClientRect();
    if (area) ampliarEm(fator, area.width / 2, area.height / 2);
  };

  // Roda do mouse: listener nativo com passive:false, senao o preventDefault
  // e ignorado e a pagina rola junto.
  useEffect(() => {
    const el = areaRef.current;
    if (!el) return undefined;
    const aoRolar = (e) => {
      e.preventDefault();
      const r = el.getBoundingClientRect();
      ampliarEm(e.deltaY < 0 ? PASSO_ZOOM : 1 / PASSO_ZOOM, e.clientX - r.left, e.clientY - r.top);
    };
    el.addEventListener('wheel', aoRolar, { passive: false });
    return () => el.removeEventListener('wheel', aoRolar);
  }, [ampliarEm]);

  useEffect(() => {
    const aoTeclar = (e) => {
      if (/^(INPUT|SELECT|TEXTAREA)$/.test(e.target?.tagName || '')) return;
      if (e.key === 'Escape') onFechar();
      else if (e.key === 'ArrowRight') vizinha(1);
      else if (e.key === 'ArrowLeft') vizinha(-1);
      else if (e.key === '+' || e.key === '=') ampliarNoCentro(PASSO_ZOOM);
      else if (e.key === '-') ampliarNoCentro(1 / PASSO_ZOOM);
      else if (e.key === '0') setZoom({ s: 1, x: 0, y: 0 });
    };
    document.addEventListener('keydown', aoTeclar);
    return () => document.removeEventListener('keydown', aoTeclar);
  });

  const aoPressionar = (e) => {
    if (zoom.s <= 1) return;
    arrasto.current = { x: e.clientX - zoom.x, y: e.clientY - zoom.y };
    e.currentTarget.setPointerCapture?.(e.pointerId);
  };
  const aoMover = (e) => {
    if (!arrasto.current) return;
    setZoom((z) => limitar({ s: z.s, x: e.clientX - arrasto.current.x, y: e.clientY - arrasto.current.y }));
  };
  const aoSoltar = () => { arrasto.current = null; };

  // Camera com o stream principal em H.265 num navegador que nao decodifica:
  // cai para o extra em vez de ficar sem imagem.
  const aoErro = (texto) => {
    if (texto === ERRO_CODEC && hd) {
      setHd(false);
      setAvisoQualidade('HD em H.265 não toca neste navegador — exibindo a qualidade leve.');
    }
  };

  return (
    <div className="camera-unica" role="dialog" aria-label={`Câmera ${camera.nome}`}>
      <div className="camera-unica__barra">
        <button type="button" className="cameras-barra__btn" onClick={onFechar} title="Voltar ao mosaico (Esc)">
          <i className="bi bi-arrow-left" />
          <span>Voltar</span>
        </button>
        <strong className="camera-unica__titulo">{rotuloCompleto(camera, gravadoresPorId)}</strong>
        {cameras.length > 1 && (
          <span className="cameras-barra__sub">{indice + 1} de {cameras.length}</span>
        )}

        <div className="cameras-barra__controles">
          {cameras.length > 1 && (
            <>
              <button type="button" className="cameras-barra__btn" onClick={() => vizinha(-1)} title="Câmera anterior (←)">
                <i className="bi bi-chevron-left" />
              </button>
              <button type="button" className="cameras-barra__btn" onClick={() => vizinha(1)} title="Próxima câmera (→)">
                <i className="bi bi-chevron-right" />
              </button>
            </>
          )}
          <button type="button" className="cameras-barra__btn" onClick={() => ampliarNoCentro(1 / PASSO_ZOOM)} title="Reduzir (−)">
            <i className="bi bi-zoom-out" />
          </button>
          <span className="cameras-barra__sub" style={{ minWidth: 42, textAlign: 'center' }}>
            {Math.round(zoom.s * 100)}%
          </span>
          <button type="button" className="cameras-barra__btn" onClick={() => ampliarNoCentro(PASSO_ZOOM)} title="Ampliar (+)">
            <i className="bi bi-zoom-in" />
          </button>
          <button
            type="button"
            className={`cameras-barra__btn ${hd ? 'cameras-barra__btn--ativo' : ''}`}
            onClick={() => { setHd((v) => !v); setAvisoQualidade(''); }}
            title={hd ? 'Exibindo o stream principal. Clique para a qualidade leve.' : 'Exibindo a qualidade leve. Clique para HD.'}
          >
            {hd ? 'HD' : 'Leve'}
          </button>
          {onRecortar && (
            <button type="button" className="cameras-barra__btn" onClick={() => onRecortar(camera)} title="Recortar gravação desta câmera">
              <i className="bi bi-scissors" />
              <span>Gravações</span>
            </button>
          )}
        </div>
      </div>

      {avisoQualidade ? <div className="camera-unica__aviso">{avisoQualidade}</div> : null}

      <div
        className={`camera-unica__area ${zoom.s > 1 ? 'camera-unica__area--zoom' : ''}`}
        ref={areaRef}
        onPointerDown={aoPressionar}
        onPointerMove={aoMover}
        onPointerUp={aoSoltar}
        onPointerCancel={aoSoltar}
        onDoubleClick={() => setZoom({ s: 1, x: 0, y: 0 })}
        title={zoom.s > 1 ? 'Arraste para mover · duplo clique volta ao normal' : 'Role o mouse para ampliar'}
      >
        <div
          className="camera-unica__zoom"
          style={{ transform: `translate(${zoom.x}px, ${zoom.y}px) scale(${zoom.s})` }}
        >
          <CameraPlayer key={`${camera.id}-${hd}`} camera={camera} hd={hd} onErro={aoErro} />
        </div>
      </div>
    </div>
  );
}
